import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowOutward from '@mui/icons-material/ArrowOutward';
import type {StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import DisplayImage from '../display-image';
import {borderRadius, colors, fontSize, fontWeight, overlay, shadows} from '@/styles/tokens';

interface CostScenario {
    id: string;
    label: string;
    cost: string;
    timeline: string;
    /** Omit when there is no scope summary; the "includes" row is then skipped. */
    scope?: string;
}

interface MvpCostTimelineProps {
    heading: string;
    /** Accepts nodes so the copy can carry an inline link. */
    subheading: React.ReactNode;
    highlight: {range: string; caption: string; image: string | StaticImageData};
    estimator: {title: string; description: string; scenarios: CostScenario[]};
    /** Optional button under the estimator. Renders only when both are supplied. */
    ctaText?: string;
    onCtaClick?: () => void;
}

const gridItemSx = {display: 'flex'} as const;

const highlightCardSx = {
    position: 'relative',
    width: '100%',
    minHeight: {xs: 320, md: 380},
    borderRadius: borderRadius.xl,
    overflow: 'hidden'
} as const;

const highlightScrimSx = {
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(0deg, ${overlay.dark80} 0%, ${overlay.dark55} 45%, ${overlay.darkHover} 100%)`
} as const;

const highlightContentSx = {position: 'absolute', left: 0, right: 0, bottom: 0, p: {xs: 2.5, md: 3.5}} as const;

const highlightRangeSx = {
    color: colors.text.inverse,
    fontWeight: fontWeight.bold,
    fontSize: {xs: fontSize[36], md: fontSize[42]},
    lineHeight: 1.1,
    mb: 1.5
} as const;

const highlightCaptionSx = {color: colors.text.inverse, fontSize: fontSize[13], lineHeight: 1.6} as const;

const estimatorCardSx = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    bgcolor: colors.background.default,
    borderRadius: borderRadius.xl,
    border: `1px solid ${colors.accent.blueBorder}`,
    boxShadow: shadows.card,
    p: {xs: 2.5, md: 3.5}
} as const;

const estimatorTitleSx = {color: colors.text.primary, fontSize: fontSize[20], fontWeight: fontWeight.bold, mb: 0.75} as const;

const estimatorDescSx = {color: colors.text.primary, fontSize: fontSize[13], lineHeight: 1.6, mb: 2.5} as const;

const scenarioGridSx = {display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 1.5, mb: 2.5} as const;

const scenarioBtnSx = (active: boolean) =>
    ({
        px: 2,
        py: 1.5,
        width: '100%',
        cursor: 'pointer',
        textAlign: 'left',
        borderRadius: borderRadius.md,
        fontSize: fontSize[14],
        fontWeight: fontWeight.regular,
        bgcolor: active ? colors.background.default : colors.background.iceBlue,
        color: active ? colors.primary.link : colors.text.primary,
        border: active ? `2px solid ${colors.primary.deep}` : `2px solid ${colors.neutral.borderLight}`,
        transition: 'border-color 0.2s ease, color 0.2s ease'
    } as const);

const detailPanelSx = {
    bgcolor: colors.background.iceBlue,
    borderRadius: borderRadius.lg,
    border: `1px solid ${colors.neutral.borderLight}`,
    p: {xs: 2, md: 2.5}
} as const;

const detailTitleSx = {color: colors.text.primary, fontSize: fontSize[16], fontWeight: fontWeight.bold, mb: 2} as const;

const statRowSx = {display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5} as const;

// Grow to share the row evenly; the basis is the wrap point on narrow screens.
const statTileSx = {flex: '1 1 160px', bgcolor: colors.background.default, borderRadius: borderRadius.md, px: 2, py: 1.5} as const;

const statLabelSx = {color: colors.text.muted, fontSize: fontSize[12], mb: 0.5} as const;

const statValueSx = {color: colors.text.primary, fontSize: fontSize[20], fontWeight: fontWeight.bold, lineHeight: 1.2} as const;

const scaleTrackSx = {
    width: '100%',
    height: 6,
    borderRadius: borderRadius.full,
    bgcolor: colors.neutral.grayLight,
    overflow: 'hidden',
    mb: 2.5
} as const;

// The bar shows where a scenario sits on the investment scale, not a completion state.
const scaleFillSx = (percent: number) =>
    ({
        width: `${percent}%`,
        height: '100%',
        borderRadius: borderRadius.full,
        background: `linear-gradient(90deg, ${colors.primary.ctaEnd} 0%, ${colors.accent.orange} 100%)`,
        transition: 'width 0.35s ease'
    } as const);

const includeRowSx = {display: 'flex', alignItems: 'flex-start', gap: 1.25} as const;

const includeDotSx = {
    width: 6,
    height: 6,
    minWidth: 6,
    mt: 0.9,
    borderRadius: borderRadius.circle,
    bgcolor: colors.primary.link
} as const;

const includeTextSx = {color: colors.text.primary, fontSize: fontSize[14], lineHeight: 1.5} as const;

// Matches the button under `MvpEndToEndServices`' list.
const ctaWrapSx = {mt: {xs: 3, md: 4}} as const;

// Scenario panels: the selected one's wrapper drops out of layout (its panel sits in the card as before); the rest
// are not displayed but stay in the server HTML.
const shownPanelSx = {display: 'contents'} as const;
const hiddenPanelSx = {display: 'none'} as const;

const MvpCostTimeline = ({heading, subheading, highlight, estimator, ctaText, onCtaClick}: MvpCostTimelineProps) => {
    const [activeId, setActiveId] = useState(estimator.scenarios[0]?.id);
    const activeIndex = Math.max(
        0,
        estimator.scenarios.findIndex((scenario) => scenario.id === activeId)
    );
    const activeScenario = estimator.scenarios[activeIndex];

    return (
        <Box>
            <MUIContainer>
                <SectionHeader heading={heading} subheading={subheading} />

                <Grid container spacing={3}>
                    <Grid item xs={12} md={5} sx={gridItemSx}>
                        <Box sx={highlightCardSx}>
                            <DisplayImage
                                src={highlight.image}
                                alt={heading}
                                fill
                                sizes="(max-width: 900px) 100vw, 42vw"
                                style={{objectFit: 'cover'}}
                            />
                            <Box sx={highlightScrimSx} />
                            <Box sx={highlightContentSx}>
                                <Typography component="p" className="font-headers" sx={highlightRangeSx}>
                                    {highlight.range}
                                </Typography>
                                <Typography component="p" className="font-regular-text" sx={highlightCaptionSx}>
                                    {highlight.caption}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={7} sx={gridItemSx}>
                        <Box sx={estimatorCardSx}>
                            <Typography component="h3" className="font-headers" sx={estimatorTitleSx}>
                                {estimator.title}
                            </Typography>
                            <Typography component="p" className="font-regular-text" sx={estimatorDescSx}>
                                {estimator.description}
                            </Typography>

                            <Box sx={scenarioGridSx}>
                                {estimator.scenarios.map((scenario) => {
                                    const active = scenario.id === activeScenario?.id;
                                    return (
                                        <Box
                                            key={scenario.id}
                                            component="button"
                                            type="button"
                                            onClick={() => setActiveId(scenario.id)}
                                            aria-pressed={active}
                                            className="font-regular-text"
                                            sx={scenarioBtnSx(active)}
                                        >
                                            {scenario.label}
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* Every scenario's estimate is in the HTML (search engines read them all); only the selected
                                one shows, laid out exactly as before (`display: contents`). */}
                            {estimator.scenarios.map((scenario, idx) => (
                                <Box key={scenario.id} sx={idx === activeIndex ? shownPanelSx : hiddenPanelSx}>
                                    <Box sx={detailPanelSx}>
                                        <Typography component="p" className="font-headers" sx={detailTitleSx}>
                                            {scenario.label}
                                        </Typography>

                                        <Box sx={statRowSx}>
                                            <Box sx={statTileSx}>
                                                <Typography component="p" className="font-headers" sx={statLabelSx}>
                                                    Estimated Cost
                                                </Typography>
                                                <Typography component="p" className="font-headers" sx={statValueSx}>
                                                    {scenario.cost}
                                                </Typography>
                                            </Box>
                                            <Box sx={statTileSx}>
                                                <Typography component="p" className="font-headers" sx={statLabelSx}>
                                                    Timeline
                                                </Typography>
                                                <Typography component="p" className="font-headers" sx={statValueSx}>
                                                    {scenario.timeline}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={scaleTrackSx}>
                                            <Box sx={scaleFillSx(((idx + 1) / estimator.scenarios.length) * 100)} />
                                        </Box>

                                        {scenario.scope && (
                                            <Box sx={includeRowSx}>
                                                <Box sx={includeDotSx} />
                                                <Typography component="span" className="font-regular-text" sx={includeTextSx}>
                                                    {scenario.scope}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            ))}

                            {ctaText && onCtaClick && (
                                <Box sx={ctaWrapSx}>
                                    <Button
                                        variant="contained"
                                        disableRipple={true}
                                        className="animated-button button-bg-primary"
                                        onClick={onCtaClick}
                                        endIcon={<ArrowOutward fontSize="small" />}
                                    >
                                        {ctaText}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(MvpCostTimeline);
